import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useIsEluveAdmin } from '@eluve/session-helpers';

import { ChatMarkdown } from './ChatMarkdown';
import { CitationModal } from './CitationModal';
import { usePdfContext } from './PdfContext';
import { getSourceUrl } from './citation';

export interface Citation {
  text: string;
  source: {
    sourceType?: string;
    sourceDocId?: string;
    sourceTimestamp?: string;
    externalUrl?: string;
  };
  startIndex?: number;
  endIndex?: number;
}

interface CitationTextProps {
  text: string;
  citations: Citation[];
  className?: string;
  onSourceClick?: (citation: Citation) => void;
}

interface TextSegment {
  text: string;
  citation?: Citation;
  citationNumber?: number;
}

export const CitationText: React.FC<CitationTextProps> = ({
  text,
  citations,
  className,
  onSourceClick,
}) => {
  const pdfContext = usePdfContext();
  const isEluveAdmin = useIsEluveAdmin();
  const { tenantId, patientId } = useParams<{
    tenantId: string;
    patientId: string;
  }>();

  const [clickedCitation, setClickedCitation] = useState<Citation | null>(null);

  // Create a mapping of unique sources to citation numbers
  const createSourceToNumberMapping = (
    citations: Citation[],
  ): Map<string, number> => {
    const sourceMap = new Map<string, number>();
    let citationNumber = 1;

    citations.forEach((citation) => {
      const sourceKey = `${citation.source.sourceType}-${citation.source.sourceDocId}`;
      if (!sourceMap.has(sourceKey)) {
        sourceMap.set(sourceKey, citationNumber++);
      }
    });

    return sourceMap;
  };

  /**
   * Parses the input text and splits it into segments based on citation positions.
   *
   * @returns {TextSegment[]} An array of text segments, where each segment may include citation information.
   */
  const parseTextWithCitations = (): TextSegment[] => {
    if (!citations || citations.length === 0) {
      return [{ text }];
    }

    // Create source to number mapping
    const sourceToNumber = createSourceToNumberMapping(citations);

    const sortedCitations = [...citations].sort(
      (a, b) => (a.startIndex ?? 0) - (b.startIndex ?? 0),
    );

    const segments: TextSegment[] = [];
    let currentIndex = 0;

    sortedCitations.forEach((citation) => {
      // Add text before citation
      const startIndex = citation.startIndex ?? 0;
      if (currentIndex < startIndex) {
        segments.push({
          text: text.slice(currentIndex, startIndex),
        });
      }

      // Get the citation number for this source
      const sourceKey = `${citation.source.sourceType}-${citation.source.sourceDocId}`;
      const citationNumber = sourceToNumber.get(sourceKey) ?? 1;

      const citedText = text.slice(citation.startIndex, citation.endIndex);
      segments.push({
        text: citedText,
        citation,
        citationNumber,
      });

      currentIndex = citation.endIndex ?? currentIndex;
    });

    // Add remaining text
    if (currentIndex < text.length) {
      segments.push({
        text: text.slice(currentIndex),
      });
    }

    return segments;
  };

  const handleCitationClick = (citation: Citation, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSourceClick?.(citation);
    const isFileCitation =
      citation.source.sourceType === 'uploadedFiles' &&
      citation.startIndex !== undefined;

    if (isFileCitation) {
      pdfContext?.handleCitationClick?.(citation);
    } else if (isEluveAdmin) {
      // For Eluve Admins: Open citation modal with JSON details
      setClickedCitation(citation);
    } else {
      const sourceUrl = getSourceUrl(citation, tenantId, patientId);
      if (sourceUrl) {
        window.open(sourceUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const { processedText, citationMap } = useMemo(() => {
    const segments = parseTextWithCitations();
    const citationMap = new Map<number, Citation>();
    let processedText = '';

    // Process segments and add ^[] tags for citations data in order to display them correctly in ChatMarkdown
    segments.forEach((segment) => {
      if (segment.citation && segment.citationNumber) {
        citationMap.set(segment.citationNumber, segment.citation);
        processedText += `${segment.text}^[${segment.citationNumber}]^`;
      } else {
        processedText += segment.text;
      }
    });

    return { processedText, citationMap };
  }, [text, citations, parseTextWithCitations]);

  const handleTextClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const citationAttr = target.getAttribute('data-citation');
    if (target.tagName?.toUpperCase() === 'SUP' && citationAttr) {
      const citationNumber = parseInt(citationAttr);
      const citation = citationMap.get(citationNumber);
      if (citation) {
        handleCitationClick(citation, e);
      }
    }
  };

  return (
    <>
      <div className={className} onClick={handleTextClick}>
        <ChatMarkdown>{processedText}</ChatMarkdown>
      </div>

      {clickedCitation && (
        <CitationModal
          citation={clickedCitation}
          isOpen={true}
          onClose={() => setClickedCitation(null)}
        />
      )}
    </>
  );
};
